import React, { useState, useMemo, useRef, useEffect } from 'react';
import { fetchSbomProjects, saveSbomProjects, useOrgId } from '../lib/data';
import { Skeleton, SkeletonBentoTile, SkeletonChart, SkeletonTable } from './Skeleton';
import { DataTable, type Column, type FilterConfig, type RowAction } from './DataTable';
import { Modal } from './Modal';
import { Drawer } from './Drawer';
import {
  ShieldAlert,
  ShieldCheck,
  FileJson,
  Upload,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Search,
  Filter,
  Sparkles,
  Database,
  Play,
  Activity,
  Layers,
  BookOpen,
  Code,
  Terminal,
  RefreshCw,
  Plus,
  Lock,
  ChevronDown,
  Pencil,
} from 'lucide-react';

// Interfaces for SBOM & Security structures
interface Dependency {
  id: string;
  name: string;
  version: string;
  latestVersion: string;
  license: string;
  type: 'direct' | 'transitive' | 'dev';
  category: 'frontend' | 'backend' | 'utility' | 'database';
  status: 'Approved' | 'Under Review' | 'Banned';
  cveCount: number;
  description: string;
}

interface CVE {
  id: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  score: number; // CVSS Score (0.0 to 10.0)
  packageName: string;
  vulnerableVersion: string;
  fixedIn: string;
  description: string;
  publishedAt: string;
  remediationCommand: string;
  status: 'active' | 'patched' | 'ignored';
  policyBreach: boolean;
}

interface PolicyRule {
  id: string;
  name: string;
  type: 'license' | 'severity' | 'deprecated' | 'custom';
  description: string;
  enabled: boolean;
  severity: 'error' | 'warning' | 'info';
  status: 'passed' | 'violated';
  violationCount: number;
}

interface SBOMPreset {
  name: string;
  repoName: string;
  format: 'CycloneDX v1.5' | 'SPDX v2.3';
  dependencies: Dependency[];
  cves: CVE[];
}

// Complete realistic presets for interactive SBOM simulations
const PRESETS: SBOMPreset[] = [
  {
    name: "Authentication & Authorization API Service",
    repoName: "auth-gateway-service",
    format: "CycloneDX v1.5",
    dependencies: [
      { id: "dep-1", name: "jsonwebtoken", version: "8.5.1", latestVersion: "9.0.2", license: "MIT", type: "direct", category: "backend", status: "Approved", cveCount: 1, description: "JSON Web Token implementation for User authentication" },
      { id: "dep-2", name: "bcrypt", version: "5.0.1", latestVersion: "5.1.1", license: "BSD-3-Clause", type: "direct", category: "backend", status: "Approved", cveCount: 1, description: "A library to help hash passwords safely" },
      { id: "dep-3", name: "express", version: "4.18.2", latestVersion: "5.0.0", license: "MIT", type: "direct", category: "backend", status: "Approved", cveCount: 0, description: "Fast, unopinionated, minimalist web framework for node" },
      { id: "dep-4", name: "pg", version: "8.11.0", latestVersion: "8.11.5", license: "MIT", type: "direct", category: "database", status: "Approved", cveCount: 0, description: "Non-blocking PostgreSQL client for Node.js" },
      { id: "dep-5", name: "lodash", version: "4.17.21", latestVersion: "4.17.21", license: "MIT", type: "transitive", category: "utility", status: "Approved", cveCount: 0, description: "Lodash modular utilities" },
      { id: "dep-6", name: "request", version: "2.88.2", latestVersion: "2.88.2 (deprecated)", license: "Apache-2.0", type: "direct", category: "utility", status: "Banned", cveCount: 0, description: "Deprecated HTTP client helper library" }
    ],
    cves: [
      {
        id: "CVE-2026-1182",
        severity: "Critical",
        score: 9.8,
        packageName: "jsonwebtoken",
        vulnerableVersion: "<9.0.0",
        fixedIn: "9.0.0",
        description: "Signature verification key confusion vulnerability in jwt.verify allowing remote attackers to bypass authorization controls entirely via malformed header parameters.",
        publishedAt: "2026-02-15",
        remediationCommand: "npm install jsonwebtoken@9.0.2",
        status: "active",
        policyBreach: true
      },
      {
        id: "CVE-2025-9981",
        severity: "High",
        score: 8.1,
        packageName: "bcrypt",
        vulnerableVersion: "<5.1.0",
        fixedIn: "5.1.0",
        description: "Integer overflow in password hashing routine leads to potential denial of service or stack corruption under heavy concurrent load on 32-bit platforms.",
        publishedAt: "2025-11-04",
        remediationCommand: "npm install bcrypt@5.1.1",
        status: "active",
        policyBreach: true
      }
    ]
  },
  {
    name: "Web Platform Frontend Client",
    repoName: "web-dashboard-ui",
    format: "CycloneDX v1.5",
    dependencies: [
      { id: "dep-101", name: "react", version: "18.2.0", latestVersion: "18.3.1", license: "MIT", type: "direct", category: "frontend", status: "Approved", cveCount: 0, description: "A JavaScript library for building user interfaces" },
      { id: "dep-102", name: "vite", version: "5.0.0", latestVersion: "5.2.11", license: "MIT", type: "dev", category: "frontend", status: "Approved", cveCount: 0, description: "Next generation frontend tooling" },
      { id: "dep-103", name: "lodash", version: "4.17.19", latestVersion: "4.17.21", license: "MIT", type: "transitive", category: "utility", status: "Approved", cveCount: 1, description: "Lodash modular utilities" },
      { id: "dep-104", name: "recharts", version: "2.10.3", latestVersion: "2.12.7", license: "MIT", type: "direct", category: "frontend", status: "Approved", cveCount: 0, description: "Redefined chart library built with React and D3" },
      { id: "dep-105", name: "urllib", version: "2.38.0", latestVersion: "3.0.0", license: "GPL-3.0", type: "transitive", category: "utility", status: "Under Review", cveCount: 1, description: "Helper request wrapper library with copyleft license" }
    ],
    cves: [
      {
        id: "CVE-2025-2287",
        severity: "Medium",
        score: 6.5,
        packageName: "lodash",
        vulnerableVersion: "<4.17.21",
        fixedIn: "4.17.21",
        description: "Prototype pollution in lodash.defaultsDeep allows an attacker to inject properties into the prototype object, leading to potential RCE or properties override.",
        publishedAt: "2025-04-12",
        remediationCommand: "npm install lodash@4.17.21",
        status: "active",
        policyBreach: false
      },
      {
        id: "CVE-2025-3341",
        severity: "High",
        score: 7.5,
        packageName: "urllib",
        vulnerableVersion: "<3.0.0",
        fixedIn: "3.0.0",
        description: "Server-side Request Forgery (SSRF) in urllib hostname parsing allows local IP scanning and credential exfiltration via maliciously structured URL parameters.",
        publishedAt: "2025-08-19",
        remediationCommand: "npm install urllib@3.0.0",
        status: "active",
        policyBreach: true
      }
    ]
  },
  {
    name: "Enterprise Data ETL Gateway",
    repoName: "etl-data-processor",
    format: "SPDX v2.3",
    dependencies: [
      { id: "dep-201", name: "axios", version: "1.5.0", latestVersion: "1.7.4", license: "MIT", type: "direct", category: "utility", status: "Approved", cveCount: 1, description: "Promise based HTTP client for the browser and node.js" },
      { id: "dep-202", name: "gpl-database-connector", version: "1.0.0", latestVersion: "1.0.0", license: "GPL-3.0", type: "direct", category: "database", status: "Banned", cveCount: 0, description: "Database connector with restrictive Copyleft licensing" },
      { id: "dep-203", name: "canvas", version: "2.11.0", latestVersion: "2.11.2", license: "MIT", type: "direct", category: "utility", status: "Approved", cveCount: 0, description: "Node canvas library for image processing" },
      { id: "dep-204", name: "xml-parser", version: "0.2.0", latestVersion: "0.2.0", license: "Unlicense", type: "direct", category: "utility", status: "Approved", cveCount: 1, description: "Legacy XML parser with vulnerabilities" }
    ],
    cves: [
      {
        id: "CVE-2025-4509",
        severity: "High",
        score: 8.8,
        packageName: "axios",
        vulnerableVersion: "<1.6.0",
        fixedIn: "1.6.0",
        description: "Server-Side Request Forgery in axios allows remote attackers to bypass IP boundaries and forward unauthorized local system queries via engineered redirect triggers.",
        publishedAt: "2025-05-18",
        remediationCommand: "npm install axios@1.7.4",
        status: "active",
        policyBreach: true
      },
      {
        id: "CVE-2025-1102",
        severity: "Critical",
        score: 9.3,
        packageName: "xml-parser",
        vulnerableVersion: "<=0.2.0",
        fixedIn: "None Available",
        description: "Uncontrolled recursion in XML entity resolver causes catastrophic stack overflow leading to instant denial of service (DoS) with minimal request payload size.",
        publishedAt: "2025-02-28",
        remediationCommand: "npm remove xml-parser && npm install fast-xml-parser",
        status: "active",
        policyBreach: true
      }
    ]
  }
];

export function SbomManager() {
  // Projects are loaded from the Worker (/api/sbom); PRESETS seed the DB on
  // first run. Each project is { id, name, repoName, format, dependencies, cves }.
  const [projects, setProjects] = useState<(SBOMPreset & { id: string })[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const orgId = useOrgId();
  // True only after a real data edit (upload/patch/ban/ignore). Gates the save
  // so loading data or switching projects doesn't PUT the just-loaded data back.
  const dirtyRef = useRef(false);

  // Preset selection
  const [selectedPresetIndex, setSelectedPresetIndex] = useState<number>(0);
  const currentPreset = projects[selectedPresetIndex] ?? PRESETS[selectedPresetIndex];

  // Dynamic state loaded/cloned from preset
  const [dependencies, setDependencies] = useState<Dependency[]>(currentPreset.dependencies);
  const [cves, setCves] = useState<CVE[]>(currentPreset.cves);
  const [selectedTab, setSelectedTab] = useState<'dashboard' | 'dependencies' | 'cves' | 'policies' | 'upload'>('dashboard');

  // Interactive uploader states
  const [rawSbomText, setRawSbomText] = useState<string>('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Policy rules state
  const [policies, setPolicies] = useState<PolicyRule[]>([
    { id: 'pol-1', name: 'Ban Restrictive Copyleft (GPL)', type: 'license', description: 'Prevent integration of GPL-3.0 or AGPL codebases to safeguard proprietary IP.', enabled: true, severity: 'error', status: 'violated', violationCount: 1 },
    { id: 'pol-2', name: 'No Critical Severity Vulnerabilities', type: 'severity', description: 'Reject commits and PR merges containing CVSS scores >= 9.0.', enabled: true, severity: 'error', status: 'violated', violationCount: 1 },
    { id: 'pol-3', name: 'Reject Known Deprecated Packages', type: 'deprecated', description: 'Ban libraries marked as abandoned/deprecated (e.g., request).', enabled: true, severity: 'warning', status: 'violated', violationCount: 1 },
    { id: 'pol-4', name: 'Require MIT, Apache-2.0, or BSD License', type: 'license', description: 'Enforce approved permissiveness for all software imports.', enabled: false, severity: 'info', status: 'passed', violationCount: 0 }
  ]);

  // AI patch simulator states
  const [patchingCve, setPatchingCve] = useState<CVE | null>(null);
  const [patchLog, setPatchLog] = useState<string[]>([]);
  const [patchStatus, setPatchStatus] = useState<'idle' | 'running' | 'success' | 'failed'>('idle');

  // AI Remediator Advisor slide-in (replaces the always-on dashboard card).
  const [advisorOpen, setAdvisorOpen] = useState(false);

  // Load projects from the Worker for the selected org. First run (org empty)
  // seeds the demo PRESETS so the page is immediately testable; thereafter the
  // DB is the source of truth for dependency/CVE state across reloads. Reloads
  // on org switch.
  useEffect(() => {
    if (!orgId) { setLoading(true); return; }
    (async () => {
      const seedFromPresets = (): (SBOMPreset & { id: string })[] =>
        PRESETS.map((p, i) => ({ id: `proj-${i}`, ...p }));
      try {
        const data = await fetchSbomProjects(orgId) as (SBOMPreset & { id: string })[];
        const loaded = data.length > 0 ? data : seedFromPresets();
        if (data.length === 0) await saveSbomProjects(loaded, orgId).catch(() => {});
        setProjects(loaded);
        setDependencies(loaded[0].dependencies);
        setCves(loaded[0].cves);
      } catch {
        // Worker offline (local dev) — use PRESETS in-memory.
        const local = seedFromPresets();
        setProjects(local);
        setDependencies(local[0].dependencies);
        setCves(local[0].cves);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);

  // Debounced auto-persist: fold the current dependency/CVE edits into the
  // active project and PUT the whole collection. Fires only after a real edit
  // (dirtyRef), so loading/switching — which also change deps/cves — is a no-op.
  useEffect(() => {
    if (loading || projects.length === 0 || !dirtyRef.current || !orgId) return;
    const t = setTimeout(() => {
      const next = projects.map((p, i) => i === selectedPresetIndex ? { ...p, dependencies, cves } : p);
      setProjects(next);
      saveSbomProjects(next, orgId).catch(() => {});
      dirtyRef.current = false;
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencies, cves, selectedPresetIndex]);

  // Reset states when changing presets
  const handlePresetChange = (idx: number) => {
    // Fold current edits into the outgoing project before switching.
    setProjects(prev => prev.map((p, i) => i === selectedPresetIndex ? { ...p, dependencies, cves } : p));
    setSelectedPresetIndex(idx);
    const target = projects[idx] ?? PRESETS[idx];
    setDependencies(target.dependencies);
    setCves(target.cves);
    setRawSbomText('');
    setUploadError(null);
    setUploadSuccess(false);
    setPatchStatus('idle');
    setPatchingCve(null);
  };

  // SBOM upload simulation parser
  const handleSbomUpload = (text: string) => {
    try {
      if (!text.trim()) {
        setUploadError("Please provide raw SBOM text or drop a file.");
        return;
      }
      
      const parsed = JSON.parse(text);
      let parsedDeps: Dependency[] = [];
      let parsedCves: CVE[] = [];
      
      // Attempt to parse standard CycloneDX JSON structure
      if (parsed.bomFormat && parsed.components) {
        parsedDeps = parsed.components.map((c: any, i: number) => ({
          id: `uploaded-dep-${i}`,
          name: c.name || "unknown-package",
          version: c.version || "1.0.0",
          latestVersion: c.version || "1.0.0",
          license: c.licenses?.[0]?.license?.id || "MIT",
          type: "direct",
          category: "frontend",
          status: "Approved",
          cveCount: 0,
          description: c.description || "Uploaded custom package"
        }));
        
        // No synthetic CVEs are injected — the SBOM shows only real scan
        // results, so a fabricated security finding is never presented as fact.
      } else {
        // Generic fallback parsing if standard JSON is uploaded
        parsedDeps = Object.keys(parsed).map((key, i) => ({
          id: `uploaded-dep-${i}`,
          name: key,
          version: typeof parsed[key] === 'string' ? parsed[key] : "1.0.0",
          latestVersion: typeof parsed[key] === 'string' ? parsed[key] : "1.0.0",
          license: "MIT",
          type: "direct",
          category: "utility",
          status: "Approved",
          cveCount: 0,
          description: "Parsed dependency item"
        }));
      }

      if (parsedDeps.length === 0) {
        setUploadError("Uploaded JSON contains no valid components or dependencies.");
        return;
      }

      setDependencies(parsedDeps);
      setCves(parsedCves);
      dirtyRef.current = true;
      setUploadSuccess(true);
      setUploadError(null);
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (e: any) {
      setUploadError(`Failed to parse SBOM JSON: ${e.message}. Please verify syntax.`);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          const content = event.target.result as string;
          setRawSbomText(content);
          handleSbomUpload(content);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          const content = event.target.result as string;
          setRawSbomText(content);
          handleSbomUpload(content);
        }
      };
      reader.readAsText(file);
    }
  };

  // Add customised policy rule (modal-driven — see commitPolicyDraft)

  // Toggle Policy rule state
  const togglePolicy = (id: string) => {
    setPolicies(prev => prev.map(p => (p.id === id ? { ...p, enabled: !p.enabled } : p)));
  };

  // ---------- modal editors (dependency + policy) ----------
  const [depDraft, setDepDraft] = useState<Dependency | null>(null);
  const openEditDep = (d: Dependency) => setDepDraft({ ...d });
  const commitDepDraft = () => {
    if (!depDraft) return;
    dirtyRef.current = true;
    setDependencies(prev => prev.map(d => (d.id === depDraft.id ? depDraft : d)));
    setDepDraft(null);
  };
  const toggleDepStatus = (d: Dependency) => {
    dirtyRef.current = true;
    setDependencies(prev => prev.map(x => (x.id === d.id ? { ...x, status: x.status === 'Approved' ? 'Banned' : 'Approved' } : x)));
  };

  const [policyDraft, setPolicyDraft] = useState<PolicyRule | null>(null);
  const [policyEditingId, setPolicyEditingId] = useState<string | null>(null);
  const openAddPolicy = () => {
    setPolicyEditingId(null);
    setPolicyDraft({ id: `custom-pol-${Date.now()}`, name: '', type: 'custom', description: '', enabled: true, severity: 'warning', status: 'passed', violationCount: 0 });
  };
  const openEditPolicy = (p: PolicyRule) => { setPolicyEditingId(p.id); setPolicyDraft({ ...p }); };
  const commitPolicyDraft = () => {
    if (!policyDraft) return;
    setPolicies(prev => {
      const idx = policyEditingId ? prev.findIndex(p => p.id === policyEditingId) : -1;
      if (idx === -1) return [...prev, policyDraft];
      return prev.map(p => (p.id === policyEditingId ? policyDraft : p));
    });
    setPolicyDraft(null);
    setPolicyEditingId(null);
  };

  // Compute live compliance warnings from active dependencies and CVEs
  const processedPolicies = useMemo(() => {
    return policies.map(p => {
      if (!p.enabled) return { ...p, status: 'passed' as const, violationCount: 0 };
      
      let violations = 0;

      if (p.type === 'license') {
        if (p.name.includes('GPL')) {
          violations = dependencies.filter(d => d.license === 'GPL-3.0').length;
        } else {
          violations = dependencies.filter(d => d.license === 'Banned' || d.status === 'Banned').length;
        }
      } else if (p.type === 'severity') {
        violations = cves.filter(c => c.severity === 'Critical' && c.status === 'active').length;
      } else if (p.type === 'deprecated') {
        violations = dependencies.filter(d => d.version.includes('deprecated') || d.latestVersion.includes('deprecated') || d.status === 'Banned').length;
      }

      return {
        ...p,
        status: violations > 0 ? 'violated' as const : 'passed' as const,
        violationCount: violations
      };
    });
  }, [policies, dependencies, cves]);

  // General computed stats for SBOM view
  const cveBreakdown = useMemo(() => {
    const activeCves = cves.filter(c => c.status === 'active');
    let critical = 0, high = 0, medium = 0, low = 0;
    
    activeCves.forEach(c => {
      if (c.severity === 'Critical') critical++;
      else if (c.severity === 'High') high++;
      else if (c.severity === 'Medium') medium++;
      else if (c.severity === 'Low') low++;
    });

    return { critical, high, medium, low, total: activeCves.length };
  }, [cves]);

  // AI patch remediation is NOT yet wired to a real backend. It must not
  // fabricate a successful patch (fake "Unit Tests Passed" + CVE cleared).
  // Report the honest unsupported state instead.
  const startRemediationPatch = (cve: CVE) => {
    setPatchingCve(cve);
    setPatchStatus('failed');
    setPatchLog([
      `AI remediation for ${cve.packageName}@${cve.vulnerableVersion} is not available yet.`,
      'No agent runtime is connected to apply a patch; the SBOM is unchanged.',
      'Use the dependency list to bump the version manually, or wire a remediation endpoint.',
    ]);
    dirtyRef.current = true;
  };

  // ---------- DataTable columns / filters / actions ----------
  const depColumns: Column<Dependency>[] = [
    {
      key: 'name',
      header: 'Dependency',
      render: (d) => (
        <div className="space-y-0.5">
          <span className="font-bold font-mono text-xs">{d.name}</span>
          <p className="text-[10px] text-[rgba(55,53,47,0.5)] max-w-xs truncate">{d.description}</p>
        </div>
      ),
    },
    {
      key: 'version',
      header: 'Version',
      width: '150px',
      render: (d) => (
        <div className="flex items-center gap-1 font-mono text-[11px]">
          <span>{d.version}</span>
          {d.version !== d.latestVersion && (
            <span className="text-[9px] text-[#ED6C02] font-semibold bg-orange-50 px-1 rounded">↳ {d.latestVersion}</span>
          )}
        </div>
      ),
    },
    {
      key: 'license',
      header: 'License',
      width: '90px',
      render: (d) => <span className={`inline-block px-1.5 py-0.5 rounded font-mono text-[10px] ${d.license === 'GPL-3.0' ? 'bg-red-50 text-red-700 font-bold' : 'bg-gray-100 text-gray-700'}`}>{d.license}</span>,
    },
    { key: 'type', header: 'Type', width: '80px', render: (d) => <span className="capitalize font-mono text-[10px] text-[rgba(55,53,47,0.6)]">{d.type}</span> },
    {
      key: 'cves',
      header: 'CVEs',
      width: '90px',
      render: (d) => d.cveCount > 0
        ? <span className="inline-block px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-bold font-mono text-[10px]">{d.cveCount}</span>
        : <span className="text-emerald-600 font-bold font-mono text-[10px]">0</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '110px',
      render: (d) => (
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
          d.status === 'Approved' ? 'bg-emerald-50 text-emerald-700' :
          d.status === 'Banned' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'
        }`}>{d.status === 'Banned' ? 'Policy Block' : d.status}</span>
      ),
    },
  ];
  const depFilters: FilterConfig<Dependency>[] = [
    { key: 'type', label: 'Type', options: [{ value: 'direct', label: 'direct' }, { value: 'transitive', label: 'transitive' }, { value: 'dev', label: 'dev' }], predicate: (d, v) => d.type === v },
    { key: 'status', label: 'Status', options: [{ value: 'Approved', label: 'Approved' }, { value: 'Under Review', label: 'Under Review' }, { value: 'Banned', label: 'Banned' }], predicate: (d, v) => d.status === v },
  ];
  const depActions: RowAction<Dependency>[] = [
    {
      key: 'toggle',
      label: (d) => (d.status === 'Approved' ? 'Ban' : 'Approve'),
      onClick: (d) => toggleDepStatus(d),
      title: 'Approve / Ban',
    },
    { key: 'edit', label: 'Edit dependency', icon: Pencil, onClick: (d) => openEditDep(d), title: 'Edit' },
  ];

  const cveColumns: Column<CVE>[] = [
    { key: 'id', header: 'CVE', width: '120px', render: (c) => <span className="text-xs font-mono font-bold text-[#37352F] uppercase">{c.id}</span> },
    {
      key: 'pkg',
      header: 'Package',
      render: (c) => (
        <div className="font-mono text-[11px] space-y-0.5">
          <div className="font-bold">{c.packageName}</div>
          <div className="text-[10px] text-red-600">{c.vulnerableVersion} → <span className="text-emerald-600">{c.fixedIn}</span></div>
        </div>
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      width: '130px',
      render: (c) => <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded uppercase ${c.severity === 'Critical' ? 'bg-red-50 text-red-700 border border-red-200' : c.severity === 'High' ? 'bg-orange-50 text-orange-700' : 'bg-yellow-50 text-yellow-700'}`}>{c.severity} ({c.score})</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      render: (c) => <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${c.status === 'patched' ? 'bg-emerald-50 text-emerald-700' : c.status === 'ignored' ? 'bg-gray-100 text-gray-500' : 'bg-red-50 text-red-700'}`}>{c.status}</span>,
    },
    { key: 'published', header: 'Published', width: '110px', render: (c) => <span className="text-[10px] font-mono text-[rgba(55,53,47,0.5)]">{c.publishedAt}</span> },
  ];
  const cveFilters: FilterConfig<CVE>[] = [
    { key: 'severity', label: 'Severity', options: [{ value: 'Critical', label: 'Critical' }, { value: 'High', label: 'High' }, { value: 'Medium', label: 'Medium' }, { value: 'Low', label: 'Low' }], predicate: (c, v) => c.severity === v },
    { key: 'status', label: 'Status', options: [{ value: 'active', label: 'active' }, { value: 'patched', label: 'patched' }, { value: 'ignored', label: 'ignored' }], predicate: (c, v) => c.status === v },
  ];
  const cveActions: RowAction<CVE>[] = [
    {
      key: 'ignore',
      label: (c) => (c.status === 'ignored' ? 'Reactivate' : 'Ignore'),
      onClick: (c) => { dirtyRef.current = true; setCves(prev => prev.map(x => (x.id === c.id ? { ...x, status: x.status === 'ignored' ? 'active' : 'ignored' } : x))); },
      title: 'Ignore / Reactivate',
    },
    { key: 'patch', label: 'Patch', icon: Sparkles, onClick: (c) => startRemediationPatch(c), disabled: (c) => c.status === 'patched', title: 'Generate AI patch' },
  ];

  const policyColumns: Column<PolicyRule>[] = [
    {
      key: 'name',
      header: 'Policy',
      render: (p) => (
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${!p.enabled ? 'bg-gray-300' : p.status === 'violated' ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} />
            <span className="font-bold text-xs text-[#37352F] font-mono">{p.name}</span>
          </div>
          <p className="text-[11px] text-[rgba(55,53,47,0.6)] leading-relaxed">{p.description}</p>
        </div>
      ),
    },
    { key: 'type', header: 'Type', width: '100px', render: (p) => <span className="text-[10px] font-mono font-bold uppercase text-[rgba(55,53,47,0.6)]">{p.type}</span> },
    {
      key: 'status',
      header: 'Compliance',
      width: '160px',
      render: (p) => <span className={`font-bold px-1.5 py-0.5 rounded uppercase text-[10px] ${!p.enabled ? 'text-gray-400' : p.status === 'violated' ? 'text-red-700 bg-red-50' : 'text-emerald-700 bg-emerald-50'}`}>{p.status === 'violated' ? `Violated (${p.violationCount})` : 'Passed'}</span>,
    },
  ];
  const policyActions: RowAction<PolicyRule>[] = [
    { key: 'toggle', label: (p) => (p.enabled ? 'Mute' : 'Activate'), onClick: (p) => togglePolicy(p.id), title: 'Enable / Disable' },
    { key: 'edit', label: 'Edit policy', icon: Pencil, onClick: (p) => openEditPolicy(p), title: 'Edit' },
  ];

  if (loading) {
    return (
      <div className="space-y-8 animate-fadeIn" aria-busy="true" aria-label="Loading SBOM projects">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-[rgba(55,53,47,0.09)] pb-5">
          <div className="space-y-2">
            <Skeleton width={200} height={12} rounded={3} />
          </div>
          <Skeleton width={260} height={32} rounded={4} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SkeletonBentoTile />
          <SkeletonBentoTile />
          <SkeletonBentoTile />
          <SkeletonBentoTile />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-[rgba(55,53,47,0.09)] rounded-lg p-5 bg-white space-y-4">
            <Skeleton width={200} height={11} rounded={3} />
            <SkeletonChart height={180} />
          </div>
          <div className="border border-[rgba(55,53,47,0.09)] rounded-lg p-5 bg-white space-y-4">
            <Skeleton width={200} height={11} rounded={3} />
            <SkeletonChart height={180} />
          </div>
        </div>
        <SkeletonTable rows={5} cols={['30%', '12%', '10%', '8%', '12%', '14%', '14%']} />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn" id="sbom-container">
      
      {/* Header Visual Bar */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-[rgba(55,53,47,0.09)] pb-5">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#2383E2]" />
            <h2 className="text-xs font-bold text-[#37352F] tracking-tight uppercase font-mono">
              SBOM & CVE Security Console
            </h2>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 self-start md:self-auto min-w-0 max-w-full">
          {/* AI Remediator Advisor — icon button opens a slide-in drawer */}
          <button
            type="button"
            onClick={() => setAdvisorOpen(true)}
            title="AI Remediator Advisor"
            aria-label="Open AI Remediator Advisor"
            className="relative flex items-center gap-1.5 bg-[rgba(55,53,47,0.04)] hover:bg-[rgba(55,53,47,0.08)] px-3 py-1.5 rounded-[4px] border border-[rgba(55,53,47,0.06)] text-xs font-semibold text-[#37352F] transition min-w-0 max-w-full"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#ED6C02]" />
            <span>AI Advisor</span>
            {cveBreakdown.total > 0 && (
              <span className="text-[9px] font-bold leading-none px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{cveBreakdown.total}</span>
            )}
          </button>

          {/* Preset selector dropdown */}
          <div className="flex flex-wrap items-center gap-2 self-start md:self-auto min-w-0 max-w-full bg-[rgba(55,53,47,0.04)] px-3 py-1.5 rounded-[4px] border border-[rgba(55,53,47,0.06)]">
            <Database className="w-3.5 h-3.5 text-[rgba(55,53,47,0.5)]" />
            <span className="text-xs font-semibold text-[#37352F]">Repository SBOM:</span>
            <select
              value={selectedPresetIndex}
              onChange={(e) => handlePresetChange(Number(e.target.value))}
              className="min-w-0 max-w-full bg-transparent text-xs font-medium text-[#2383E2] border-none focus:ring-0 outline-none cursor-pointer"
            >
              {projects.map((p, idx) => (
                <option key={p.repoName} value={idx}>{p.repoName} ({p.format})</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Stats Bento Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" id="sbom-bento-row">
        
        {/* Total Components Card */}
        <div className="bg-white border border-[rgba(55,53,47,0.08)] p-4 rounded-lg flex flex-col justify-between h-28 hover:shadow-xs transition">
          <div className="text-[10px] font-bold text-[rgba(55,53,47,0.5)] uppercase tracking-wider">
            Total SBOM Packages
          </div>
          <div>
            <div className="text-2xl font-extrabold text-[#37352F] font-mono flex items-baseline gap-2">
              {dependencies.length}
              <span className="text-xs font-normal text-[rgba(55,53,47,0.4)]">
                {dependencies.filter(d => d.type === 'direct').length} direct
              </span>
            </div>
            <p className="text-[10px] text-[rgba(55,53,47,0.5)] mt-1">
              Third party modules compiled in inventory
            </p>
          </div>
        </div>

        {/* CVE Status Overview Card */}
        <div className="bg-white border border-[rgba(55,53,47,0.08)] p-4 rounded-lg flex flex-col justify-between h-28 hover:shadow-xs transition">
          <div className="text-[10px] font-bold text-[rgba(55,53,47,0.5)] uppercase tracking-wider flex justify-between">
            <span>CVE Security Risks</span>
            {cveBreakdown.total > 0 ? (
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            ) : (
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            )}
          </div>
          <div>
            <div className="text-2xl font-extrabold text-[#37352F] font-mono flex items-baseline gap-1">
              {cveBreakdown.total}
              <span className="text-xs font-normal text-[rgba(55,53,47,0.4)]">active vulnerabilities</span>
            </div>
            <div className="flex gap-2 mt-1">
              <span className="text-[9px] font-bold px-1 rounded bg-red-100 text-red-700">{cveBreakdown.critical} Crit</span>
              <span className="text-[9px] font-bold px-1 rounded bg-orange-100 text-orange-700">{cveBreakdown.high} High</span>
              <span className="text-[9px] font-bold px-1 rounded bg-yellow-100 text-yellow-700">{cveBreakdown.medium} Med</span>
            </div>
          </div>
        </div>

        {/* Compliance Warnings Card */}
        <div className="bg-white border border-[rgba(55,53,47,0.08)] p-4 rounded-lg flex flex-col justify-between h-28 hover:shadow-xs transition">
          <div className="text-[10px] font-bold text-[rgba(55,53,47,0.5)] uppercase tracking-wider">
            License & Rule Compliance
          </div>
          <div>
            <div className="text-2xl font-extrabold text-[#37352F] font-mono">
              {processedPolicies.filter(p => p.status === 'violated').length === 0 ? (
                <span className="text-emerald-600 flex items-center gap-1 text-xl">
                  <ShieldCheck className="w-5 h-5" /> Passed All
                </span>
              ) : (
                <span className="text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  {processedPolicies.filter(p => p.status === 'violated').length} Fail
                </span>
              )}
            </div>
            <p className="text-[10px] text-[rgba(55,53,47,0.5)] mt-1">
              Active security guard policies checked
            </p>
          </div>
        </div>

        {/* Preset File Metadata Card */}
        <div className="bg-white border border-[rgba(55,53,47,0.08)] p-4 rounded-lg flex flex-col justify-between h-28 hover:shadow-xs transition">
          <div className="text-[10px] font-bold text-[rgba(55,53,47,0.5)] uppercase tracking-wider">
            SBOM Manifest Specs
          </div>
          <div>
            <div className="text-xs font-semibold text-[#37352F] font-mono bg-[rgba(55,53,47,0.04)] px-1.5 py-1 rounded inline-block">
              {currentPreset.format}
            </div>
            <div className="text-[10px] text-[rgba(55,53,47,0.5)] mt-2 font-mono">
              Generated: Today, 12:00 PM UTC
            </div>
          </div>
        </div>

      </div>

      {/* Tabs navigation */}
      <div className="flex overflow-x-auto border-b border-[rgba(55,53,47,0.09)] gap-1">
        {([
          { id: 'dashboard', label: 'Security Dashboard', icon: Activity },
          { id: 'dependencies', label: 'Dependencies Inventory', icon: Layers },
          { id: 'cves', label: 'CVE Vulnerabilities', icon: ShieldAlert },
          { id: 'policies', label: 'Compliance Policies', icon: BookOpen },
          { id: 'upload', label: 'Upload SBOM', icon: Upload }
        ] as const).map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 border-b-2 whitespace-nowrap text-xs font-semibold uppercase tracking-wider transition ${
                selectedTab === tab.id
                  ? 'border-[#2383E2] text-[#2383E2]'
                  : 'border-transparent text-[rgba(55,53,47,0.6)] hover:text-[#37352F]'
              }`}
            >
              <Icon className="w-3 h-3" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Interactive AI Patch Stream Modal/Terminal Overlay */}
      {patchStatus !== 'idle' && patchingCve && (
        <div className="border border-[rgba(55,53,47,0.12)] bg-[#191919] text-[#E0E0E0] rounded-lg p-5 font-mono text-xs shadow-md space-y-4 max-w-4xl mx-auto animate-fadeIn relative">
          <button 
            onClick={() => { setPatchStatus('idle'); setPatchingCve(null); }}
            className="absolute top-4 right-4 text-[rgba(255,255,255,0.4)] hover:text-white"
          >
            Close ✕
          </button>
          
          <div className="flex items-center gap-2 border-b border-[rgba(255,255,255,0.1)] pb-3">
            <Terminal className="w-4 h-4 text-[#2383E2] animate-pulse" />
            <span className="font-bold text-[#FFFFFF]">Claude Automated Security Patch Shell</span>
            <span className="text-[10px] px-1.5 py-0.5 bg-[rgba(255,255,255,0.1)] text-[#2383E2] rounded">ACTIVE ROUTINE</span>
          </div>

          <div className="space-y-1.5 min-h-[160px] bg-[#0E0E0E]/80 p-3 rounded overflow-y-auto max-h-[250px]">
            {patchLog.map((logLine, idx) => (
              <div key={idx} className="flex gap-2 animate-fadeIn">
                <span className="text-[rgba(255,255,255,0.35)] select-none">$&gt;</span>
                <span className={
                  logLine.includes('[SUCCESS]') ? 'text-emerald-400 font-bold' :
                  logLine.includes('[TEST]') ? 'text-blue-400' :
                  logLine.includes('[DISCOVERY]') ? 'text-yellow-400' :
                  logLine.includes('[PLAN]') ? 'text-indigo-300' : ''
                }>
                  {logLine}
                </span>
              </div>
            ))}
            {patchStatus === 'running' && (
              <div className="flex items-center gap-2 text-[rgba(255,255,255,0.4)] pt-2 animate-pulse">
                <RefreshCw className="w-3 h-3 animate-spin text-[#2383E2]" />
                <span>AI Agent refactoring and compiling codebase...</span>
              </div>
            )}
          </div>

          {patchStatus === 'failed' && (
            <div className="bg-red-950/40 border border-red-500/30 p-3 rounded flex items-center justify-between text-red-400 text-[11px]">
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                <span>AI remediation not available — no patch applied. SBOM unchanged.</span>
              </div>
              <button
                onClick={() => { setPatchStatus('idle'); setPatchingCve(null); }}
                className="px-2 py-1 bg-red-500 text-white rounded font-bold hover:bg-red-600 transition"
              >
                Dismiss Terminal
              </button>
            </div>
          )}

          {patchStatus === 'success' && (
            <div className="bg-emerald-950/40 border border-emerald-500/30 p-3 rounded flex items-center justify-between text-emerald-400 text-[11px]">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>Patched successfully! Vulnerability eliminated and SBOM dependency bumped in config tree.</span>
              </div>
              <button 
                onClick={() => { setPatchStatus('idle'); setPatchingCve(null); }}
                className="px-2 py-1 bg-emerald-500 text-white rounded font-bold hover:bg-emerald-600 transition"
              >
                Dismiss Terminal
              </button>
            </div>
          )}
        </div>
      )}

      {/* TAB 1: DASHBOARD & CHARTS */}
      {selectedTab === 'dashboard' && (
        <div className="space-y-8 animate-fadeIn" id="dashboard-tab-content">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Visual SVGs Dashboard Columns (Left 8 Cols) */}
            <div className="lg:col-span-8 space-y-6">
              
              {/* Chart 1: Visual CVSS Severity Range distribution */}
              <div className="border border-[rgba(55,53,47,0.09)] rounded-lg p-5 bg-white space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono flex items-center gap-1.5">
                    <Activity className="w-4 h-4 text-[#2383E2]" />
                    <span>CVSS Score Distribution Trend</span>
                  </div>
                  <span className="text-[10px] text-[rgba(55,53,47,0.5)] font-mono">
                    High Scores Require Immediate Attention
                  </span>
                </div>

                <div className="relative">
                  {/* custom responsive SVG score chart */}
                  <svg viewBox="0 0 500 180" className="w-full h-auto bg-[#FAFAF9]/50 rounded border border-[rgba(55,53,47,0.04)]">
                    {/* Y scale guide lines */}
                    <line x1="40" y1="20" x2="460" y2="20" stroke="rgba(55, 53, 47, 0.04)" strokeDasharray="2 2" />
                    <line x1="40" y1="70" x2="460" y2="70" stroke="rgba(55, 53, 47, 0.04)" strokeDasharray="2 2" />
                    <line x1="40" y1="120" x2="460" y2="120" stroke="rgba(55, 53, 47, 0.04)" strokeDasharray="2 2" />
                    <line x1="40" y1="150" x2="460" y2="150" stroke="rgba(55, 53, 47, 0.1)" />

                    {/* Left side axis texts */}
                    <text x="32" y="24" textAnchor="end" className="text-[9px] font-mono fill-[rgba(55,53,47,0.5)]">10.0</text>
                    <text x="32" y="74" textAnchor="end" className="text-[9px] font-mono fill-[rgba(55,53,47,0.5)]">5.0</text>
                    <text x="32" y="124" textAnchor="end" className="text-[9px] font-mono fill-[rgba(55,53,47,0.5)]">2.0</text>
                    <text x="32" y="154" textAnchor="end" className="text-[9px] font-mono fill-[rgba(55,53,47,0.5)]">0</text>

                    {/* Bars showing CVE distributions */}
                    {cves.length === 0 ? (
                      <text x="250" y="90" textAnchor="middle" className="text-xs font-semibold fill-[rgba(55,53,47,0.4)]">
                        No active security vulnerabilities detected
                      </text>
                    ) : (
                      cves.map((c, i) => {
                        const colWidth = 420 / cves.length;
                        const barWidth = Math.min(35, colWidth * 0.5);
                        const x = 40 + i * colWidth + (colWidth - barWidth) / 2;
                        // Map CVSS score (0 to 10) to height range (0 to 130)
                        const barHeight = (c.score / 10) * 130;
                        const y = 150 - barHeight;

                        let fill = 'rgba(35, 131, 226, 0.75)'; // blue
                        if (c.severity === 'Critical') fill = 'rgba(219, 68, 85, 0.8)'; // red
                        else if (c.severity === 'High') fill = 'rgba(237, 108, 2, 0.8)'; // orange

                        if (c.status === 'patched') fill = 'rgba(46, 125, 50, 0.2)'; // faded green for patched

                        return (
                          <g key={c.id}>
                            <rect 
                              x={x} 
                              y={y} 
                              width={barWidth} 
                              height={barHeight} 
                              fill={fill} 
                              rx={2}
                            />
                            <text 
                              x={x + barWidth / 2} 
                              y={y - 6} 
                              textAnchor="middle" 
                              className="text-[9px] font-mono font-bold fill-[#37352F]"
                            >
                              {c.status === 'patched' ? '✓' : c.score}
                            </text>
                            <text 
                              x={x + barWidth / 2} 
                              y="164" 
                              textAnchor="middle" 
                              className="text-[8px] font-mono fill-[rgba(55,53,47,0.6)]"
                            >
                              {c.id}
                            </text>
                          </g>
                        );
                      })
                    )}
                  </svg>
                </div>
              </div>

              {/* Chart 2: License breakdown & Direct vs Transitive mix */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* License Compliance checklist */}
                <div className="border border-[rgba(55,53,47,0.09)] rounded-lg p-4 bg-white space-y-3">
                  <div className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono">
                    License Auditing Results
                  </div>
                  <div className="space-y-2">
                    {Array.from(new Set(dependencies.map(d => d.license))).map((lic, i) => {
                      const count = dependencies.filter(d => d.license === lic).length;
                      const pct = Math.round((count / dependencies.length) * 100);
                      const isViolation = lic === 'GPL-3.0';

                      return (
                        <div key={i} className="flex items-center justify-between text-xs p-1.5 rounded hover:bg-[#F7F7F5] transition">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${isViolation ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} />
                            <span className="font-mono text-xs font-semibold">{lic}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-[rgba(55,53,47,0.5)] font-mono">{count} components</span>
                            <span className={`text-[10px] font-bold px-1 rounded ${isViolation ? 'bg-red-50 text-red-600' : 'bg-[rgba(55,53,47,0.04)] text-[rgba(55,53,47,0.7)]'}`}>
                              {isViolation ? 'Warning copyleft' : 'Permissive'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Scope Mix percentage slider */}
                <div className="border border-[rgba(55,53,47,0.09)] rounded-lg p-4 bg-white flex flex-col justify-between">
                  <div className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono">
                    Component Types Breakdown
                  </div>
                  
                  <div className="space-y-4 my-2">
                    {['direct', 'transitive', 'dev'].map(type => {
                      const count = dependencies.filter(d => d.type === type).length;
                      const pct = Math.round((count / dependencies.length) * 100);
                      
                      let color = 'bg-[#2383E2]';
                      if (type === 'transitive') color = 'bg-orange-400';
                      if (type === 'dev') color = 'bg-gray-400';

                      return (
                        <div key={type} className="space-y-1">
                          <div className="flex items-center justify-between text-xs font-medium text-[#37352F]">
                            <span className="capitalize">{type} Dependencies</span>
                            <span className="font-mono text-[10px] text-[rgba(55,53,47,0.5)]">{count} ({pct}%)</span>
                          </div>
                          <div className="w-full h-1.5 bg-[rgba(55,53,47,0.06)] rounded-full overflow-hidden">
                            <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="text-[10px] text-[rgba(55,53,47,0.5)] leading-tight">
                    * Transitive dependencies are imported implicitly by top-level packages. They present a major blindspot for manual security updates.
                  </div>
                </div>

              </div>

            </div>

            {/* Quick Action Item Alert Column (Right 4 Cols) */}
            <div className="lg:col-span-4 space-y-6">
              

              {/* AI Remediator Advisor lives in a slide-in drawer (header button) */}
              {/* Quick Policy Compliance checklist Widget */}
              <div className="border border-[rgba(55,53,47,0.09)] rounded-lg p-4 bg-white space-y-3">
                <div className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono">
                  Guardrail Health Check
                </div>
                <div className="space-y-2">
                  {processedPolicies.map(pol => (
                    <div key={pol.id} className="flex items-center justify-between text-xs">
                      <span className="text-[rgba(55,53,47,0.7)] font-medium truncate max-w-[150px]">{pol.name}</span>
                      {pol.status === 'passed' ? (
                        <span className="text-emerald-600 font-bold bg-emerald-50 px-1 rounded text-[10px]">PASSED</span>
                      ) : (
                        <span className="text-red-600 font-bold bg-red-50 px-1 rounded text-[10px]">
                          VIOLATED ({pol.violationCount})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

            </div>

          </div>
        </div>
      )}

      {/* TAB 2: DEPENDENCIES INVENTORY */}
      {selectedTab === 'dependencies' && (
        <div className="space-y-3 animate-fadeIn" id="dependencies-tab-content">
          <DataTable
            rows={dependencies}
            columns={depColumns}
            rowKey={(d) => d.id}
            searchKeys={[(d) => d.name, (d) => d.license, (d) => d.description]}
            searchPlaceholder="Search packages by name or license…"
            filters={depFilters}
            actions={depActions}
            pageSize={10}
            dense
            emptyState={<>No dependencies match your filters.</>}
          />
        </div>
      )}

      {/* TAB 3: CVE VULNERABILITIES */}
      {selectedTab === 'cves' && (
        <div className="space-y-3 animate-fadeIn" id="cves-tab-content">
          <DataTable
            rows={cves}
            columns={cveColumns}
            rowKey={(c) => c.id}
            searchKeys={[(c) => c.id, (c) => c.packageName, (c) => c.description]}
            searchPlaceholder="Search CVEs by id or package…"
            filters={cveFilters}
            actions={cveActions}
            pageSize={10}
            dense
            emptyState={<><ShieldCheck className="w-5 h-5 mx-auto mb-2 opacity-60" />No matching vulnerabilities found.</>}
          />
        </div>
      )}

      {/* TAB 4: COMPLIANCE POLICIES */}
      {selectedTab === 'policies' && (
        <div className="space-y-4 animate-fadeIn" id="policies-tab-content">
          <div className="space-y-0.5">
            <h3 className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono">
              System Guardrail Policy Configuration
            </h3>
            <p className="text-xs text-[rgba(55,53,47,0.5)]">
              Enable block requirements for SBOM imports and continuous integrations
            </p>
          </div>
          <DataTable
            rows={processedPolicies}
            columns={policyColumns}
            rowKey={(p) => p.id}
            searchKeys={[(p) => p.name, (p) => p.description]}
            searchPlaceholder="Search policies…"
            filters={[{ key: 'enabled', label: 'State', options: [{ value: 'enabled', label: 'Active' }, { value: 'disabled', label: 'Muted' }], predicate: (p, v) => (v === 'enabled' ? p.enabled : !p.enabled) }]}
            actions={policyActions}
            pageSize={10}
            dense
            emptyState={<>No policies configured.</>}
            toolbar={
              <button
                onClick={openAddPolicy}
                className="flex items-center gap-1 bg-[#2383E2] hover:bg-[#1C69B5] text-white text-xs px-3 py-1.5 rounded-[4px] font-bold uppercase tracking-wider transition"
              >
                <Plus className="w-3.5 h-3.5" />
                Create Custom Rule
              </button>
            }
          />
        </div>
      )}

      {/* TAB 5: UPLOAD SBOM */}
      {selectedTab === 'upload' && (
        <div className="space-y-4 animate-fadeIn" id="upload-tab-content">
          <div className="space-y-0.5">
            <h3 className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5 text-[#2383E2]" />
              Upload / Paste External SBOM
            </h3>
            <p className="text-xs text-[rgba(55,53,47,0.5)]">
              Replace the active inventory with custom CycloneDX output or a raw dependency hash.
            </p>
          </div>

          <div className="border border-[rgba(55,53,47,0.09)] rounded-lg p-5 bg-[#FAF9F6]/30 space-y-4">
            <p className="text-xs text-[rgba(55,53,47,0.6)] leading-relaxed">
              Provide custom CycloneDX JSON output from commands like <code className="bg-white px-1 py-0.5 rounded border font-mono">npm audit --json</code> or security tools.
            </p>

            {/* Drag and drop zone */}
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border border-dashed border-[rgba(55,53,47,0.15)] bg-white p-6 rounded-lg text-center cursor-pointer hover:bg-[rgba(55,53,47,0.02)] transition"
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept=".json"
              />
              <FileJson className="w-8 h-8 text-[rgba(55,53,47,0.3)] mx-auto mb-2" />
              <p className="text-xs font-bold text-[#37352F]">Drag and drop SBOM json or click to choose file</p>
              <p className="text-[10px] text-[rgba(55,53,47,0.5)] mt-1">Accepts standard CycloneDX or raw dependencies hash</p>
            </div>

            {/* Raw text block fallback */}
            <div className="space-y-2">
              <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase">Or paste raw JSON string</label>
              <textarea
                rows={6}
                value={rawSbomText}
                onChange={(e) => setRawSbomText(e.target.value)}
                placeholder='{ "bomFormat": "CycloneDX", "components": [ { "name": "axios", "version": "1.4.0" } ] }'
                className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-3 py-1.5 text-xs font-mono text-[#37352F] outline-none resize-none leading-relaxed"
              />

              {uploadError && (
                <p className="text-red-600 font-bold font-mono text-[10px] mt-1">{uploadError}</p>
              )}

              {uploadSuccess && (
                <p className="text-emerald-600 font-bold font-mono text-[10px] mt-1">✓ Custom SBOM loaded successfully! Visual metrics updated.</p>
              )}

              <button
                type="button"
                onClick={() => handleSbomUpload(rawSbomText)}
                className="w-full bg-[#37352F] hover:bg-[#2F2E2A] text-white py-2 rounded-[4px] text-xs font-mono font-semibold transition"
              >
                Parse & Import SBOM
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dependency editor modal */}
      <Modal
        open={depDraft !== null}
        onClose={() => setDepDraft(null)}
        title="Edit dependency"
        subtitle="Writes to the SBOM draft — autosaved to the project."
        size="md"
        footer={
          <>
            <button onClick={() => setDepDraft(null)} className="px-3 py-1 text-xs text-[rgba(55,53,47,0.6)] font-semibold border border-[rgba(55,53,47,0.09)] rounded hover:bg-gray-50">Cancel</button>
            <button onClick={commitDepDraft} className="bg-[#2383E2] hover:bg-[#1C69B5] text-white text-xs px-3 py-1 rounded font-bold uppercase tracking-wider">Apply</button>
          </>
        }
      >
        {depDraft && (
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase mb-1">Name</label>
              <input value={depDraft.name} onChange={(e) => setDepDraft({ ...depDraft, name: e.target.value })} className="w-full bg-white border border-[rgba(55,53,47,0.09)] rounded-[4px] px-3 py-1.5 text-xs text-[#37352F] outline-none font-mono" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase mb-1">Version</label>
                <input value={depDraft.version} onChange={(e) => setDepDraft({ ...depDraft, version: e.target.value })} className="w-full bg-white border border-[rgba(55,53,47,0.09)] rounded-[4px] px-3 py-1.5 text-xs text-[#37352F] outline-none font-mono" />
              </div>
              <div>
                <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase mb-1">Latest</label>
                <input value={depDraft.latestVersion} onChange={(e) => setDepDraft({ ...depDraft, latestVersion: e.target.value })} className="w-full bg-white border border-[rgba(55,53,47,0.09)] rounded-[4px] px-3 py-1.5 text-xs text-[#37352F] outline-none font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase mb-1">License</label>
                <input value={depDraft.license} onChange={(e) => setDepDraft({ ...depDraft, license: e.target.value })} className="w-full bg-white border border-[rgba(55,53,47,0.09)] rounded-[4px] px-3 py-1.5 text-xs text-[#37352F] outline-none font-mono" />
              </div>
              <div>
                <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase mb-1">Type</label>
                <select value={depDraft.type} onChange={(e) => setDepDraft({ ...depDraft, type: e.target.value as Dependency['type'] })} className="w-full bg-white border border-[rgba(55,53,47,0.09)] rounded-[4px] px-2 py-1.5 text-xs text-[#37352F] outline-none">
                  <option value="direct">direct</option>
                  <option value="transitive">transitive</option>
                  <option value="dev">dev</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase mb-1">Policy status</label>
              <select value={depDraft.status} onChange={(e) => setDepDraft({ ...depDraft, status: e.target.value as Dependency['status'] })} className="w-full bg-white border border-[rgba(55,53,47,0.09)] rounded-[4px] px-2 py-1.5 text-xs text-[#37352F] outline-none">
                <option value="Approved">Approved</option>
                <option value="Under Review">Under Review</option>
                <option value="Banned">Banned</option>
              </select>
            </div>
          </div>
        )}
      </Modal>

      {/* Policy editor modal (add / edit) */}
      <Modal
        open={policyDraft !== null}
        onClose={() => { setPolicyDraft(null); setPolicyEditingId(null); }}
        title={policyEditingId ? 'Edit policy' : 'New guardrail rule'}
        subtitle="Compliance status is computed live from the active SBOM."
        size="md"
        footer={
          <>
            <button onClick={() => { setPolicyDraft(null); setPolicyEditingId(null); }} className="px-3 py-1 text-xs text-[rgba(55,53,47,0.6)] font-semibold border border-[rgba(55,53,47,0.09)] rounded hover:bg-gray-50">Cancel</button>
            <button onClick={commitPolicyDraft} disabled={!policyDraft?.name.trim()} className="bg-[#2383E2] hover:bg-[#1C69B5] text-white text-xs px-3 py-1 rounded font-bold uppercase tracking-wider disabled:opacity-40">{policyEditingId ? 'Apply' : 'Save Policy'}</button>
          </>
        }
      >
        {policyDraft && (
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase mb-1">Rule name</label>
              <input value={policyDraft.name} onChange={(e) => setPolicyDraft({ ...policyDraft, name: e.target.value })} placeholder="e.g. Reject GPL-2.0 or Older Licenses" className="w-full bg-white border border-[rgba(55,53,47,0.09)] rounded-[4px] px-3 py-1.5 text-xs text-[#37352F] outline-none" autoFocus />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase mb-1">Type</label>
                <select value={policyDraft.type} onChange={(e) => setPolicyDraft({ ...policyDraft, type: e.target.value as PolicyRule['type'] })} className="w-full bg-white border border-[rgba(55,53,47,0.09)] rounded-[4px] px-2 py-1.5 text-xs text-[#37352F] outline-none">
                  <option value="license">License Compliance</option>
                  <option value="severity">CVSS Severity Limit</option>
                  <option value="deprecated">Package Lifecycle</option>
                  <option value="custom">Custom Policy</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase mb-1">Enforcement</label>
                <select value={policyDraft.severity} onChange={(e) => setPolicyDraft({ ...policyDraft, severity: e.target.value as PolicyRule['severity'] })} className="w-full bg-white border border-[rgba(55,53,47,0.09)] rounded-[4px] px-2 py-1.5 text-xs text-[#37352F] outline-none">
                  <option value="error">Error (Block Merging)</option>
                  <option value="warning">Warning (Alert Logs)</option>
                  <option value="info">Info Audit Only</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase mb-1">Description</label>
              <textarea value={policyDraft.description} onChange={(e) => setPolicyDraft({ ...policyDraft, description: e.target.value })} rows={2} placeholder="Describe policy intent and remediation directions…" className="w-full bg-white border border-[rgba(55,53,47,0.09)] rounded-[4px] px-3 py-1.5 text-xs text-[#37352F] outline-none resize-none" />
            </div>
            <label className="flex items-center gap-2 text-xs text-[#37352F] cursor-pointer">
              <input type="checkbox" checked={policyDraft.enabled} onChange={(e) => setPolicyDraft({ ...policyDraft, enabled: e.target.checked })} className="accent-[#2383E2]" />
              Enabled
            </label>
          </div>
        )}
      </Modal>

      {/* AI Remediator Advisor slide-in drawer (opened from the header button) */}
      <Drawer
        open={advisorOpen}
        onClose={() => setAdvisorOpen(false)}
        title={
          <span className="flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-[#ED6C02]" />
            AI Remediator Advisor
          </span>
        }
        ariaLabel="AI Remediator Advisor"
        width={460}
      >
        <div className="space-y-4 p-5">
          <p className="text-[11px] text-[rgba(55,53,47,0.6)] leading-relaxed">
            Direct corrections identified from the active CVE feed. Apply an automated patch to bump each vulnerable package to its safe version.
          </p>

          {cves.filter(c => c.status === 'active').length === 0 ? (
            <div className="text-center py-10">
              <ShieldCheck className="w-9 h-9 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-[#37352F]">Zero active CVEs</p>
              <p className="text-[11px] text-[rgba(55,53,47,0.5)] mt-1">All packages meet minimum compliance standards.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cves.filter(c => c.status === 'active').map(cve => (
                <div
                  key={cve.id}
                  className="border border-[rgba(55,53,47,0.08)] bg-white p-3 rounded-lg flex flex-col gap-2"
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${cve.severity === 'Critical' ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-orange-100 text-orange-700'}`}>
                      {cve.severity} (CVSS {cve.score})
                    </span>
                    <span className="text-[10px] font-mono text-[rgba(55,53,47,0.5)]">{cve.id}</span>
                  </div>
                  <p className="text-[11px] font-medium text-[#37352F] leading-snug">
                    Vuln found in <span className="font-bold">{cve.packageName}</span>, safe bump target is <span className="text-emerald-600 font-bold">{cve.fixedIn}</span>.
                  </p>
                  <button
                    type="button"
                    onClick={() => startRemediationPatch(cve)}
                    className="w-full flex items-center justify-center gap-1 bg-[#2383E2] hover:bg-[#1C69B5] text-white text-[10px] py-1.5 rounded font-bold uppercase tracking-wider transition"
                  >
                    <Play className="w-3 h-3 fill-current" />
                    Apply Auto Patch
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Drawer>

    </div>
  );
}
